import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CameraAlt as CameraAltIcon,
  CheckCircle,
  ContentCopy,
  Edit as EditIcon,
  Image as ImageIcon,
  Palette as PaletteIcon,
  PhoneIphone as PhoneIphoneIcon,
  SettingsRemote as SettingsRemoteIcon,
} from '@mui/icons-material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { QRCodeSVG } from 'qrcode.react';
import { getDeviceMgmtFeatures, normalizeAppProduct } from '../../utils/deviceMgmtFeatures';
import AppAccentColorApps from './AppAccentColorApps';
import MachineQrPositionEditor from './MachineQrPositionEditor';
import PhotoboothSimpleMode from './PhotoboothSimpleMode';
import MobileUploadDeviceLimit from './MobileUploadDeviceLimit';
import StaffUnlimitedQrCard from './StaffUnlimitedQrCard';
import MachineScopedProPhotos from './MachineScopedProPhotos';
import ControlPageUiToggle from './ControlPageUiToggle';
import BackgroundRemovalDeviceToggle from './BackgroundRemovalDeviceToggle';
import { useI18n } from '../../i18n/useI18n';

export interface MachineDeviceMgmtTabsProps {
  userId?: string;
  machineDetail: {
    machineCode: string;
    randomCode: string;
    fullCode: string;
    randomCodeUpdatedAt?: string;
    appProduct?: string | null;
    userId?: string | null;
  };
  uploadPageUrl: string;
  controlPageUrl: string;
  editPageUrl: string;
  copiedTarget: string | null;
  onCopy: (text: string, target: string) => Promise<void> | void;
  formatDateTime: (d: string) => string;
  /** Admin/staff: hiện công tắc bật/tắt mục Điều khiển cho tài khoản user. */
  manageControlPageUi?: boolean;
  isStaffUser?: boolean;
}

const MachineDeviceMgmtTabs: React.FC<MachineDeviceMgmtTabsProps> = ({
  userId,
  machineDetail,
  uploadPageUrl,
  controlPageUrl,
  editPageUrl,
  copiedTarget,
  onCopy,
  formatDateTime,
  manageControlPageUi = false,
  isStaffUser = false,
}) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  const { pages: t, formatMessage } = useI18n();
  const [tab, setTab] = React.useState(0);
  const {
    showUpload,
    showControl,
    showEdit,
    showPhotos,
    showPhotobooth,
    showQrEdit,
    showColorApps,
  } = getDeviceMgmtFeatures(machineDetail?.appProduct, { isStaffUser });
  const tabKeys = [
    ...(showControl ? (['control'] as const) : []),
    ...(showPhotos ? (['photos'] as const) : []),
    ...(showUpload ? (['upload'] as const) : []),
    ...(showPhotobooth ? (['photobooth'] as const) : []),
    ...(showEdit ? (['edit'] as const) : []),
    ...(showQrEdit ? (['qrEdit'] as const) : []),
    ...(showColorApps ? (['colorApps'] as const) : []),
  ];
  const safeTab = Math.min(tab, Math.max(tabKeys.length - 1, 0));

  const photosOwnerUserId = String(userId || machineDetail?.userId || '').trim() || undefined;
  const isProMachine = normalizeAppProduct(machineDetail?.appProduct) === 'pro'
    || !normalizeAppProduct(machineDetail?.appProduct);

  React.useEffect(() => {
    setTab(0);
  }, [machineDetail?.machineCode, machineDetail?.appProduct]);

  return (
    <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
      {manageControlPageUi && (userId || machineDetail?.userId) ? (
        <ControlPageUiToggle
          userId={String(userId || machineDetail.userId)}
          machineCode={machineDetail.machineCode}
        />
      ) : null}
      {manageControlPageUi && isProMachine && (userId || machineDetail?.userId) ? (
        <BackgroundRemovalDeviceToggle
          userId={String(userId || machineDetail.userId)}
          machineCode={machineDetail.machineCode}
        />
      ) : null}
      <Tabs
        value={safeTab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 1.5,
          minHeight: 44,
          '& .MuiTabs-scrollButtons': {
            '&.Mui-disabled': { opacity: 0.3 },
          },
          '& .MuiTabs-flexContainer': {
            alignItems: 'stretch',
            justifyContent: 'flex-start',
          },
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
          '& .MuiTab-root': {
            minHeight: 44,
            minWidth: 'auto',
            flex: '0 0 auto',
            maxWidth: 360,
            px: { xs: 1, sm: 1.5 },
            py: 0,
            fontSize: { xs: 11, sm: 13 },
            textTransform: 'none',
            fontWeight: 700,
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
          },
          '& .MuiTab-iconWrapper': {
            marginRight: { xs: 0.4, sm: 0.75 },
            marginBottom: '0 !important',
          },
        }}
      >
        {showControl && (
          <Tab
            icon={<SettingsRemoteIcon fontSize="small" />}
            iconPosition="start"
            label={isCompact ? t.deviceMgmt.tabs.controlShort : t.deviceMgmt.tabs.control}
          />
        )}
        {showPhotos && (
          <Tab
            icon={<ImageIcon fontSize="small" />}
            iconPosition="start"
            label={isCompact ? t.deviceMgmt.tabs.photosShort : t.deviceMgmt.tabs.photos}
          />
        )}
        {showUpload && (
          <Tab
            icon={<PhoneIphoneIcon fontSize="small" />}
            iconPosition="start"
            label={isCompact ? t.deviceMgmt.tabs.uploadShort : t.deviceMgmt.tabs.upload}
          />
        )}
        {showPhotobooth && (
          <Tab
            icon={<CameraAltIcon fontSize="small" />}
            iconPosition="start"
            label={isCompact ? t.deviceMgmt.tabs.photoboothShort : t.deviceMgmt.tabs.photobooth}
          />
        )}
        {showEdit && (
          <Tab
            icon={<EditIcon fontSize="small" />}
            iconPosition="start"
            label={isCompact ? t.deviceMgmt.tabs.editShort : t.deviceMgmt.tabs.edit}
          />
        )}
        {showQrEdit && (
          <Tab
            icon={<QrCode2Icon fontSize="small" />}
            iconPosition="start"
            label={isCompact ? t.deviceMgmt.tabs.qrEditShort : t.deviceMgmt.tabs.qrEdit}
          />
        )}
        {showColorApps && (
          <Tab
            icon={<PaletteIcon fontSize="small" />}
            iconPosition="start"
            label={isCompact ? t.deviceMgmt.tabs.colorAppsShort : t.deviceMgmt.tabs.colorApps}
          />
        )}
      </Tabs>

      {!showUpload && !showControl && !showEdit && !showPhotos && tabKeys[safeTab] !== 'colorApps' && tabKeys[safeTab] !== 'photobooth' && tabKeys[safeTab] !== 'qrEdit' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t.deviceMgmt.noQrFeatures}
        </Alert>
      )}

      {tabKeys[safeTab] === 'photos' && (
        <MachineScopedProPhotos
          userId={photosOwnerUserId}
          machineCode={machineDetail.machineCode}
          appProduct={machineDetail.appProduct}
        />
      )}

      {tabKeys[safeTab] === 'upload' && (
        <>
          <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography variant="body2" color="text.secondary" gutterBottom fontWeight={700}>
                {t.deviceMgmt.qrUploadTitle}
              </Typography>
              <Box display="flex" justifyContent="center" alignItems="center" my={1.5}>
                <Box
                  onClick={() => window.open(uploadPageUrl, '_blank')}
                  sx={{
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'white',
                    cursor: 'pointer',
                    '&:active': { opacity: 0.85 },
                  }}
                >
                  <QRCodeSVG value={uploadPageUrl} size={isCompact ? 148 : 200} includeMargin />
                </Box>
              </Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.75}
                sx={{ mb: 0.5 }}
              >
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                  {t.deviceMgmt.fullCode}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', fontWeight: 800, letterSpacing: 0.4 }}
                >
                  {machineDetail.fullCode}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => onCopy(machineDetail.randomCode, 'random')}
                  aria-label={t.deviceMgmt.randomCodeTitle}
                >
                  {copiedTarget === 'random' ? <CheckCircle color="success" fontSize="small" /> : <ContentCopy fontSize="small" />}
                </IconButton>
              </Stack>
              <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ mb: 1 }}>
                {formatMessage(t.deviceMgmt.lastUpdated, {
                  date: formatDateTime(machineDetail.randomCodeUpdatedAt || ''),
                })}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={copiedTarget === 'link' ? <CheckCircle color="success" /> : <ContentCopy />}
                  onClick={() => onCopy(uploadPageUrl, 'link')}
                >
                  {t.deviceMgmt.copyLink}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <Box sx={{ mt: 1.5 }}>
            <MobileUploadDeviceLimit
              userId={userId}
              machineCode={machineDetail.machineCode}
              appProduct={machineDetail.appProduct}
            />
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <StaffUnlimitedQrCard
              machineCode={machineDetail.machineCode}
              purpose="mobile-upload"
              compact={isCompact}
            />
          </Box>
        </>
      )}

      {tabKeys[safeTab] === 'control' && (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography variant="body2" color="text.secondary" gutterBottom fontWeight={700}>
              {t.deviceMgmt.controlQrTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t.deviceMgmt.controlQrHint} <strong>{machineDetail.machineCode}</strong>
            </Typography>
            <Box display="flex" justifyContent="center" alignItems="center" my={1.5}>
              <Box
                sx={{
                  p: 1.25,
                  border: '2px solid',
                  borderColor: 'secondary.main',
                  borderRadius: 2,
                  bgcolor: 'white',
                  cursor: 'pointer',
                }}
                onClick={() => window.open(controlPageUrl, '_blank')}
              >
                <QRCodeSVG value={controlPageUrl} size={isCompact ? 148 : 200} includeMargin />
              </Box>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 1, display: 'block', wordBreak: 'break-all', fontFamily: 'monospace' }}
            >
              {controlPageUrl}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={copiedTarget === 'ctrl-link' ? <CheckCircle color="success" /> : <ContentCopy />}
                onClick={() => onCopy(controlPageUrl, 'ctrl-link')}
              >
                {t.deviceMgmt.copyLink}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {tabKeys[safeTab] === 'edit' && (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography variant="body2" color="text.secondary" gutterBottom fontWeight={700}>
              {t.deviceMgmt.editQrTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t.deviceMgmt.editQrHint} <strong>{machineDetail.machineCode}</strong>
            </Typography>
            <Box display="flex" justifyContent="center" alignItems="center" my={1.5}>
              <Box
                sx={{
                  p: 1.25,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  bgcolor: 'white',
                  cursor: 'pointer',
                }}
                onClick={() => window.open(editPageUrl, '_blank')}
              >
                <QRCodeSVG value={editPageUrl} size={isCompact ? 148 : 200} includeMargin />
              </Box>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 1, display: 'block', wordBreak: 'break-all', fontFamily: 'monospace' }}
            >
              {editPageUrl}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={copiedTarget === 'edit-link' ? <CheckCircle color="success" /> : <ContentCopy />}
                onClick={() => onCopy(editPageUrl, 'edit-link')}
              >
                {t.deviceMgmt.copyLink}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {tabKeys[safeTab] === 'qrEdit' && (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <MachineQrPositionEditor userId={userId} machineCode={machineDetail.machineCode} />
          </CardContent>
        </Card>
      )}

      {tabKeys[safeTab] === 'colorApps' && (
        <AppAccentColorApps userId={userId} machineCode={machineDetail.machineCode} />
      )}

      {tabKeys[safeTab] === 'photobooth' && (
        <PhotoboothSimpleMode userId={userId} machineCode={machineDetail.machineCode} />
      )}
    </Box>
  );
};

export default MachineDeviceMgmtTabs;
